import {marked} from "marked";
import DOMPurify from "dompurify";
import hljs from "highlight.js";
import Swiper from "swiper";
import {Autoplay, Navigation, Pagination} from "swiper/modules";
import {XhrService} from "../../ts/xhr.service";
import {setAccessibilityLiveRegionMessage} from "../accessibilityLiveRegionService";
import {appendScriptToDOM} from "../utilities/functions";

function escapeHtml(value) {
    return String(value)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function parseProductBlockItems(raw) {
    const items = [];
    const productBlockPattern = /```product(?:\s*\r?\n|\s+)([\s\S]*?)```/g;

    for (const match of raw.matchAll(productBlockPattern)) {
        const normalizedContent = match[1]
            .replace(/\r\n/g, "\n")
            .trim();

        if (!normalizedContent) {
            continue;
        }

        const parts = normalizedContent.split("|");
        if (parts.length < 3) {
            continue;
        }

        const itemNo = parts.shift()?.replace(/\s+/g, " ").trim();
        const href = parts.shift()?.replace(/\s+/g, " ").trim();
        const title = parts.shift()?.replace(/\s+/g, " ").trim();
        const imageUrl = parts.join("|").replace(/\s+/g, " ").trim();

        if (!itemNo || !href || !title) {
            continue;
        }

        items.push({itemNo, href, title, imageUrl});
    }

    return items;
}

function renderProductSlider(items) {
    return `<div class="aiChatProductList swiper"><div class="swiper-wrapper">` + items.map((product) => {
        const itemNo = escapeHtml(product.itemNo);
        const href = escapeHtml(product.href);
        const title = escapeHtml(product.title);
        const productClasses = ["aiChatProduct"];
        const imageHtml = product.imageUrl
            ? `<img src="${escapeHtml(product.imageUrl)}" lazyloading="true">`
            : "";

        if(product.imageUrl){
            productClasses.push("has-image");
            productClasses.push("is-loaded");
        }

        return `<div class='swiper-slide'><a href="${href}" class="${productClasses.join(" ")}" data-item-no="${itemNo}"><span class='aiChatProduct__img'>${imageHtml}</span><span class='aiChatProduct__description'>${title}</span></a></div>`;
    }).join("") + `</div><div class='swiper-navigation'><div class='swiper-button-prev'><i class='icon icon-angle-left'></i></div><div class='swiper-button-next'><i class='icon icon-angle-right'></i></div></div></div>`;
}

function replaceProductBlocks(markdownText) {
    return markdownText.replace(
        /((?:```product(?:\s*\r?\n|\s+)[\s\S]*?```(?:\s*|\r?\n)*)+)/g,
        (raw) => {
            const items = parseProductBlockItems(raw);

            if (items.length === 0) {
                return raw;
            }

            return `\n\n${renderProductSlider(items)}\n\n`;
        }
    );
}

function parseCartBlockActions(raw) {
    const actions = [];
    const cartBlockPattern = /```cart(?:\s*\r?\n|\s+)([\s\S]*?)```/g;

    for (const match of raw.matchAll(cartBlockPattern)) {
        const normalizedContent = match[1]
            .replace(/\r\n/g, "\n")
            .trim();

        if (!normalizedContent) {
            continue;
        }

        const parts = normalizedContent.split("|");
        if (parts.length < 2) {
            continue;
        }

        const itemNo = parts.shift()?.replace(/\s+/g, " ").trim();
        const quantity = parts.shift()?.replace(/\s+/g, " ").trim();
        const title = parts.join("|").replace(/\s+/g, " ").trim();

        if (!itemNo || !quantity) {
            continue;
        }

        actions.push({itemNo, quantity, title});
    }

    return actions;
}

function renderCartActions(actions) {
    return `<div class="aiChatCartActions">` + actions.map((action) => {
        const itemNo = escapeHtml(action.itemNo);
        const quantity = escapeHtml(action.quantity);
        const title = escapeHtml(action.title || action.itemNo);
        const actionText = action.title
            ? `${action.title} (${action.quantity})`
            : `${action.itemNo} (${action.quantity})`;

        return `<div class="aiChatCartAction" data-item-no="${itemNo}" data-quantity="${quantity}" data-title="${title}">${escapeHtml(actionText)}</div>`;
    }).join("") + `</div>`;
}

function replaceCartBlocks(markdownText) {
    return markdownText.replace(
        /```cart(?:\s*\r?\n|\s+)[\s\S]*?```/g,
        (raw) => {
            const actions = parseCartBlockActions(raw);

            if (actions.length === 0) {
                return raw;
            }

            return `\n\n${renderCartActions(actions)}\n\n`;
        }
    );
}

marked.setOptions({
    gfm: true,
    breaks: true
});

function initProductSlider(message){
    for(const productSlider of message.querySelectorAll(".aiChatProductList.swiper")){
        if(productSlider.classList.contains("swiper-initialized") || productSlider.swiper){
            continue;
        }

        const childrenCount = productSlider.querySelectorAll('.swiper-wrapper > *').length;
        let loop = false;
        let autoplay = false;
        let slidesPerView = 2;

        if (window.outerWidth > GLOBALS.gridBreakpoints.sm) {
            slidesPerView = 3;
        }

        if(childrenCount > slidesPerView){
            loop = true;
            autoplay = true;

            Swiper.use([Autoplay, Navigation, Pagination]);
            new Swiper(productSlider, {
                loop: loop,
                speed: 300,
                spaceBetween: 10,
                watchSlidesProgress: true,
                watchSlidesVisibility: true,
                autoHeight: false,
                slidesPerView: "auto",
                autoplay: {
                    delay: 5000,
                    enabled: autoplay
                },
                navigation: {
                    nextEl: productSlider.querySelector('.swiper-button-next'),
                    prevEl: productSlider.querySelector('.swiper-button-prev'),
                }
            });
        }
    }
}

function loadProductSlider(message){
    const itemNos = [];

    for(const product of message.querySelectorAll(".aiChatProduct:not(.has-image)")){
        const itemNo = product.dataset.itemNo;
        if(itemNo){
            itemNos.push(itemNo);
        }
    }

    if(itemNos.length > 0){
        const formData = new FormData();
        formData.set('item_nos', JSON.stringify(itemNos));

        if(message.querySelectorAll(".aiChatProduct:not(.is-loaded):not(.has-image)").length > 0){
            XhrService.generalAjaxCall('getImagesByItemNos', formData, 'POST', 'json').then(response => {
                if (!response.status) {
                    throw "Could not save data";
                }
                if(response.images){
                    for(const img of response.images){
                        const product = message.querySelector(".aiChatProduct[data-item-no='"+img.item_no+"']:not(.is-loaded):not(.has-image)");
                        if(product){
                            const productImgWrapper = product.querySelector(".aiChatProduct__img");
                            if(productImgWrapper){
                                const productImg = document.createElement("img");
                                productImg.setAttribute("src",img.imagelink);
                                productImg.setAttribute("lazyloading",true);
                                productImgWrapper.appendChild(productImg);
                                product.classList.add("is-loaded");
                            }
                        }
                    }
                    initProductSlider(message);
                }
                return response.status;
            })
                .catch((e) => {
                    console.error('Error:', e);
                });
        }
    }
}

function getProductSliderSignature(productSlider){
    return [...productSlider.querySelectorAll(".aiChatProduct")]
        .map((product) => product.dataset.itemNo || "")
        .join("|");
}

function updateMessageBody(messageBody, html) {
    const tempWrapper = document.createElement("div");
    tempWrapper.innerHTML = html;

    const currentSliders = [...messageBody.querySelectorAll(".aiChatProductList.swiper")];
    const nextSliders = [...tempWrapper.querySelectorAll(".aiChatProductList.swiper")];

    if (currentSliders.length > 0 && currentSliders.length === nextSliders.length) {
        nextSliders.forEach((nextSlider, index) => {
            const currentSlider = currentSliders[index];
            if (!currentSlider) {
                return;
            }

            if (getProductSliderSignature(currentSlider) === getProductSliderSignature(nextSlider)) {
                nextSlider.replaceWith(currentSlider);
            }
        });
    }

    messageBody.replaceChildren(...tempWrapper.childNodes);
}

function updateChatbotHeaderBasket(headerBasketContent) {
    if (!headerBasketContent) {
        return;
    }

    const doc = new DOMParser().parseFromString(headerBasketContent, "text/html");
    const newHeaderBasket = doc.querySelector(".js-headerBasket");

    if (!newHeaderBasket) {
        return;
    }

    for (const headerBasket of document.querySelectorAll(".js-headerBasket")) {
        headerBasket.innerHTML = newHeaderBasket.innerHTML;
    }
}

function getCartActionKey(cartAction){
    return [
        cartAction.dataset.itemNo || "",
        cartAction.dataset.quantity || "",
        cartAction.dataset.title || ""
    ].join("|");
}

function getChatbotCartStatusText(status, title) {
    const textMap = {
        pending: GLOBALS?.tc?.chatbot_cart_pending || "%title% wird in den Warenkorb gelegt...",
        success: GLOBALS?.tc?.chatbot_cart_success || "%title% in den Warenkorb gelegt",
        error: GLOBALS?.tc?.chatbot_cart_error || "%title% konnte nicht in den Warenkorb gelegt werden"
    };

    return (textMap[status] || "%title%")
        .replace("%title%", title);
}

function focusChatInput(aiChat) {
    const chatInput = aiChat?.querySelector(".aiChatMessage__input");

    if (!chatInput || chatInput.disabled) {
        return;
    }

    requestAnimationFrame(() => {
        chatInput.focus();
        const valueLength = chatInput.value?.length || 0;
        chatInput.setSelectionRange(valueLength, valueLength);
    });
}

export function initAIChats(wrapper = document) {
    wrapper.querySelectorAll(".aiChat").forEach((aiChat) => {
        const sitepartId = aiChat.dataset.sitepartId;
        const userName = aiChat.dataset.userName;
        const botName = aiChat.dataset.botName;
        const MAX_FILES = parseInt(aiChat.dataset.maxFiles, 10) || 5; // Default to 5 if not provided
        const siteId = aiChat.dataset.siteId;
        const languageId = aiChat.dataset.languageId;

        if (!sitepartId) return;

        const chatBody = aiChat.querySelector(".aiChatBody");
        const chatInput = aiChat.querySelector(".aiChatMessage__input");
        const chatInputWrapper = aiChat.querySelector(".aiChatMessage__inputWrapper");
        const chatSend = aiChat.querySelector(".aiChatMessage__sendMessage");
        const chatLoading = aiChat.querySelector(".aiChatLoading");
        const chatQuestions = aiChat.querySelectorAll(".aiChatQuestion");
        const uploadFileButton = aiChat.querySelector(".aiChatUploadFileButton");
        const fileInput = aiChat.querySelector(".aiChatUploadFileInput");
        const uploadedFilesContainer = aiChat.querySelector(".aiChatUploadFileContainer");
        const parentModal = aiChat.closest(".modal");

        let isSending = false;
        let uploadedFileData = [];

        if (!parentModal || parentModal.classList.contains("open")) {
            focusChatInput(aiChat);
        }

        if (parentModal?.id) {
            document.querySelectorAll(`[data-modal="${parentModal.id}"]`).forEach((modalTrigger) => {
                modalTrigger.addEventListener("click", () => {
                    setTimeout(() => {
                        focusChatInput(aiChat);
                    }, 260);
                });
            });
        }

        chatSend?.addEventListener("click", () => sendMessage(null));

        chatInput?.addEventListener("keydown", e => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage(null);
            }
        });

        chatQuestions.forEach(question => {
            question.addEventListener("click", () => {
                if (isSending) return;
                const presetPrompt = question.dataset.question;
                if (presetPrompt) {
                    chatInput.value = presetPrompt;
                    sendMessage();
                }
            });
        });

        uploadFileButton?.addEventListener("click", () => {
            // Remove error elements before opening the file dialog
            const errorElements = uploadedFilesContainer?.querySelectorAll(".aiChatUploadFile.is-error");
            errorElements?.forEach(element => element.remove());

            fileInput?.click();
        });

        function addUploadedFile(fileName, fileId, tools) {
            const fileElement = document.createElement("li");
            fileElement.className = "aiChatUploadFile";

            const fileNameElement = document.createElement("span");
            fileNameElement.textContent = fileName;
            fileNameElement.className = "aiChatUploadFile__name";
            fileElement.appendChild(fileNameElement);

            const deleteButton = document.createElement("button");
            deleteButton.innerHTML = "<i class='icon icon-trash'></i>";
            deleteButton.className = "aiChatUploadFile__deleteButton";
            deleteButton.addEventListener("click", () => {
                uploadedFileData = uploadedFileData.filter(file => file.id !== fileId);
                setAccessibilityLiveRegionMessage(GLOBALS.tc.file_success_deleted.replace("%file%",fileName));
                fileElement.remove();
            });
            fileElement.appendChild(deleteButton);

            uploadedFilesContainer?.appendChild(fileElement);

            uploadedFileData.push({ id: fileId, tools });
        }

        function buildInfoMessage(text){
            let alertElement = chatInput.closest(".aiChatMessage").querySelector(".alert");
            if(!alertElement){
                alertElement = document.createElement("div");
                alertElement.classList.add("alert");
                alertElement.classList.add("alert--info");
                alertElement.classList.add("marginTop");
            }
            alertElement.innerText = text;
            chatInput.closest(".aiChatMessage").appendChild(alertElement);
            setTimeout(function (){
                alertElement.remove();
                chatInput.focus();
            },5000);
        }

        fileInput?.addEventListener("change", (e) => {
            if (isSending) return;

            const files = e.target.files;
            if (!files || files.length === 0) return;

            if (uploadedFileData.length + files.length > MAX_FILES) {
                buildInfoMessage(GLOBALS.tc.chatbot_limit_files_alert.replace("%maxFiles%",MAX_FILES));
                return;
            }

            isSending = true;
            setAccessibilityLiveRegionMessage(GLOBALS.tc.files_are_uploaded);

            const uploadPromises = Array.from(files).map(file => {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("id", sitepartId);

                const fileElement = document.createElement("li");
                fileElement.className = "aiChatUploadFile is-loading";
                fileElement.textContent = `${file.name}`;
                uploadedFilesContainer.appendChild(fileElement);

                return fetch("/chatbot/", {
                    method: "POST",
                    body: formData
                })
                .then(response => {
                    if (!response.ok) {
                        throw new Error("Upload failed");
                    }
                    return response.json();
                })
                .then(data => {
                    if (data.id && data.tools) {
                        fileElement.remove();
                        addUploadedFile(file.name, data.id, data.tools);
                        setAccessibilityLiveRegionMessage(GLOBALS.tc.file_success_uploaded.replace("%file%",file.name));
                    }
                })
                .catch(() => {
                    fileElement.classList.remove("is-loading");
                    fileElement.classList.add("is-error");
                    setTimeout(function (){
                        fileElement.remove();
                    },10000);
                    setAccessibilityLiveRegionMessage(GLOBALS.tc.file_upload_error.replace("%file%",file.name));
                });
            });

            Promise.all(uploadPromises)
                .finally(() => {
                    isSending = false;
                });
        });

        function addMessage(text, role, isHTML = false) {
            const div = document.createElement("div");
            div.className = `aiChatMessage aiChatMessage--${role}`;

            const divBody = document.createElement("div");
            divBody.className = "aiChatMessage__body";
            divBody[isHTML ? "innerHTML" : "textContent"] = text;

            if(role === "bot" && botName !== ""){
                const divHeader = document.createElement("div");
                divHeader.className = "aiChatMessage__header";
                divHeader.innerText = botName;
                div.appendChild(divHeader);
            }

            if(role === "user" && userName !== ""){
                const divHeader = document.createElement("div");
                divHeader.className = "aiChatMessage__header";
                divHeader.innerText = userName;
                div.appendChild(divHeader);
            }

            div.appendChild(divBody);

            chatBody.appendChild(div);
            return div;
        }

        function updateLoadingText() {
            const chatLoadingText = chatLoading.querySelector(".aiChatLoadingTextTyper");
            if (!chatLoadingText) return;

            const replacedTexts = [
                GLOBALS.tc.bot_loading_text_1,
                GLOBALS.tc.bot_loading_text_2,
                GLOBALS.tc.bot_loading_text_3,
                GLOBALS.tc.bot_loading_text_4,
                GLOBALS.tc.bot_loading_text_5
            ];

            const loadingTexts = replacedTexts.map(t => t.trim());

            // Clear any existing typewriter timer
            if (chatLoadingText._typewriterTimer) {
                clearTimeout(chatLoadingText._typewriterTimer);
            }

            // Typewriter state
            let currentTextIndex = typeof chatLoadingText._twIndex === 'number' ? chatLoadingText._twIndex : Math.floor(Math.random() * loadingTexts.length);
            let isDeleting = false;
            let charIndex = 0;

            const typingSpeed = 30;      // ms per character when typing
            const deletingSpeed = 30;    // ms per character when deleting
            const holdAtFullMs = 1800;    // pause when fully typed
            const holdAtEmptyMs = 200;   // pause when fully deleted before next text

            function pickNextIndex(prev) {
                if (loadingTexts.length < 2) return prev; // nothing else to rotate to
                let idx;
                do { idx = Math.floor(Math.random() * loadingTexts.length); } while (idx === prev);
                return idx;
            }

            function tick() {
                const full = loadingTexts[currentTextIndex];

                if (!isDeleting) {
                    // typing forward
                    charIndex = Math.min(charIndex + 1, full.length);
                    chatLoadingText.innerText = full.slice(0, charIndex);

                    if (charIndex === full.length) {
                        isDeleting = true;
                        chatLoadingText._typewriterTimer = setTimeout(tick, holdAtFullMs);
                        return;
                    }
                    chatLoadingText._typewriterTimer = setTimeout(tick, typingSpeed);
                } else {
                    // deleting backward
                    charIndex = Math.max(charIndex - 1, 0);
                    chatLoadingText.innerText = full.slice(0, charIndex);

                    if (charIndex === 0) {
                        isDeleting = false;
                        currentTextIndex = pickNextIndex(currentTextIndex);
                        chatLoadingText._twIndex = currentTextIndex; // persist chosen index
                        chatLoadingText._typewriterTimer = setTimeout(tick, holdAtEmptyMs);
                        return;
                    }
                    chatLoadingText._typewriterTimer = setTimeout(tick, deletingSpeed);
                }
            }

            // Initialize display and start the loop
            chatLoadingText.innerText = "";
            charIndex = 0;
            isDeleting = false;
            chatLoadingText._typewriterTimer = setTimeout(tick, typingSpeed);
        }

        function stopLoadingText() {
            const chatLoadingText = chatLoading.querySelector(".aiChatLoadingTextTyper");
            if (chatLoadingText && chatLoadingText._typewriterTimer) {
                clearTimeout(chatLoadingText._typewriterTimer);
                chatLoadingText._typewriterTimer = null;
            }
        }

        function sendMessage(presetPrompt = null) {
            if (isSending) return;
            isSending = true;
            chatSend.classList.toggle('sending');
            chatInput.disabled = true;
            chatInputWrapper.classList.add("is-loading");

            let lastMessage = null;
            const prompt = presetPrompt ?? chatInput.value.trim();
            if (!prompt) return;

            if (!presetPrompt) {
                const lastUserMessage = addMessage(prompt, "user");
                chatInput.value = "";
                chatLoading.style.display = "block";

                updateLoadingText();

                window.scrollTo({
                    top: lastUserMessage.getBoundingClientRect().top + window.pageYOffset - 100,
                    behavior: "smooth"
                });
            }

            const storedThreadId = sessionStorage.getItem("thread_id");
            if (storedThreadId === null || storedThreadId === "") {
                sessionStorage.removeItem("thread_id");
            }

            const requestBody = {
                prompt: prompt,
                id: sitepartId,
                files: uploadedFileData,
                site_id: siteId,
                language_id: languageId,
                ...(storedThreadId && { thread_id: storedThreadId })
            };

            fetch("/chatbot/", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestBody)
            })
                .then(response => {
                    const reader = response.body.getReader();
                    const decoder = new TextDecoder("utf-8");
                    let fullMarkdown = "";
                    let buffer = "";
                    let threadIdSet = false;
                    const cartActionStates = new Map();
                    let pendingRenderMarkdown = "";
                    let lastRenderedMarkdown = null;
                    let lastRenderAt = 0;
                    let renderTimer = null;
                    let renderFrameRequested = false;
                    const STREAM_RENDER_INTERVAL = 80;

                    function syncCartActions(message) {
                        for (const cartAction of message.querySelectorAll(".aiChatCartAction")) {
                            const actionKey = getCartActionKey(cartAction);
                            const itemNo = cartAction.dataset.itemNo;
                            const quantity = cartAction.dataset.quantity;
                            const title = cartAction.dataset.title || itemNo;

                            if (!actionKey || !itemNo || !quantity) {
                                continue;
                            }

                            const existingState = cartActionStates.get(actionKey);

                            if (existingState?.status === "success") {
                                cartAction.classList.remove("is-pending", "is-error");
                                cartAction.classList.add("is-success");
                                cartAction.textContent = getChatbotCartStatusText("success", title);
                                continue;
                            }

                            if (existingState?.status === "error") {
                                cartAction.classList.remove("is-pending", "is-success");
                                cartAction.classList.add("is-error");
                                cartAction.textContent = getChatbotCartStatusText("error", title);
                                continue;
                            }

                            if (existingState?.status === "pending") {
                                cartAction.classList.remove("is-success", "is-error");
                                cartAction.classList.add("is-pending");
                                cartAction.textContent = getChatbotCartStatusText("pending", title);
                                continue;
                            }

                            cartActionStates.set(actionKey, {status: "pending"});
                            cartAction.classList.remove("is-success", "is-error");
                            cartAction.classList.add("is-pending");
                            cartAction.textContent = getChatbotCartStatusText("pending", title);

                            const formData = new FormData();
                            formData.set("action", "shop_add_item_to_basket_card");
                            formData.set("item_no", itemNo);
                            formData.set("item_qty", quantity);

                            XhrService.generalAjaxCall("cart", formData, "POST", "json").then(response => {
                                if (!response.status) {
                                    throw "Could not save data";
                                }

                                cartActionStates.set(actionKey, {status: "success"});
                                updateChatbotHeaderBasket(response.headerBasketContent);

                                if (response.tagManager) {
                                    appendScriptToDOM(response.tagManager, true);
                                }

                                setAccessibilityLiveRegionMessage(GLOBALS.tc.addedItemToBasket);
                                syncCartActions(message);
                                return response.status;
                            }).catch((error) => {
                                cartActionStates.set(actionKey, {status: "error"});
                                console.error("Error:", error);
                                syncCartActions(message);
                            });
                        }
                    }

                    function renderMessage(markdownText) {
                        const markdownWithProductSliders = replaceProductBlocks(markdownText);
                        const markdownWithActions = replaceCartBlocks(markdownWithProductSliders);
                        const html = DOMPurify.sanitize(marked.parse(markdownWithActions));

                        if (!lastMessage) {
                            lastMessage = addMessage("", "bot", true);
                        }
                        updateMessageBody(lastMessage.querySelector(".aiChatMessage__body"), html);

                        initProductSlider(lastMessage);
                        loadProductSlider(lastMessage);
                        syncCartActions(lastMessage);

                        lastMessage.querySelectorAll('pre code').forEach(block => {
                            hljs.highlightElement(block);
                        });
                    }

                    function flushRender(force = false) {
                        renderFrameRequested = false;

                        if (!force && pendingRenderMarkdown === lastRenderedMarkdown) {
                            return;
                        }

                        lastRenderedMarkdown = pendingRenderMarkdown;
                        lastRenderAt = Date.now();
                        renderMessage(pendingRenderMarkdown);
                    }

                    function scheduleRender(markdownText, force = false) {
                        pendingRenderMarkdown = markdownText;

                        if (force) {
                            if (renderTimer) {
                                clearTimeout(renderTimer);
                                renderTimer = null;
                            }
                            flushRender(true);
                            return;
                        }

                        if (pendingRenderMarkdown === lastRenderedMarkdown) {
                            return;
                        }

                        const waitTime = Math.max(0, STREAM_RENDER_INTERVAL - (Date.now() - lastRenderAt));

                        if (renderTimer) {
                            return;
                        }

                        renderTimer = setTimeout(() => {
                            renderTimer = null;

                            if (renderFrameRequested) {
                                return;
                            }

                            renderFrameRequested = true;
                            requestAnimationFrame(() => flushRender());
                        }, waitTime);
                    }

                    function readChunk() {
                        return reader.read().then(({ done, value }) => {
                            if (done) {
                                if (renderTimer) {
                                    clearTimeout(renderTimer);
                                    renderTimer = null;
                                }
                                if (!threadIdSet && buffer.trim() !== "") {
                                    scheduleRender(buffer, true);
                                } else if (threadIdSet && fullMarkdown !== '') {
                                    scheduleRender(fullMarkdown, true);
                                }
                                chatLoading.style.display = "";
                                stopLoadingText();
                                return;
                            }

                            const chunk = decoder.decode(value, { stream: true });
                            buffer += chunk;

                            if (!threadIdSet) {
                                const separatorIndex = buffer.indexOf("\n\n");

                                if (separatorIndex !== -1) {
                                    const header = buffer.slice(0, separatorIndex).trim();
                                    const markdownStart = buffer.slice(separatorIndex + 2);

                                    try {
                                        const firstJson = JSON.parse(header);
                                        sessionStorage.setItem("thread_id", firstJson.thread_id);
                                        threadIdSet = true;
                                        fullMarkdown = markdownStart;
                                        buffer = "";
                                        scheduleRender(fullMarkdown, true);
                                    } catch (e) {
                                        console.error(e);
                                    }
                                }
                            }

                            if (threadIdSet) {
                                fullMarkdown += buffer;
                                if (fullMarkdown !== '') {
                                    scheduleRender(fullMarkdown);
                                    buffer = "";
                                }
                            }

                            return readChunk();
                        });
                    }

                    return readChunk();
                })
                .then(() => {
                    isSending = false;
                    chatSend.classList.toggle('sending');
                    chatInput.disabled = false;
                    chatInputWrapper.classList.remove("is-loading");
                    chatInput.focus();

                    uploadedFileData = [];
                })
                .catch(error => {
                    chatLoading.style.display = "";
                    stopLoadingText();
                    isSending = false;
                    chatSend.classList.toggle('sending');
                    chatInputWrapper.classList.remove("is-loading");
                    chatInput.disabled = false;
                    addMessage(GLOBALS.tc.bot_no_message, "bot");
                    console.error("Error during fetch:", error);
                });
        }
    });
}
